<template>
  <kd-popup
    class="gift-sku-select-bottom-popup"
    :visible.sync="popupVisible"
    position="bottom"
    :hide-title="true"
    :closeOnClickModal="false"
    @display="onPopupDisplay"
  >
    <div class="gift-sku-select-bottom-popup__panel">
      <div class="gift-sku-select-bottom-popup__handle" />
      <h3 class="gift-sku-select-bottom-popup__title">请选择礼品规格</h3>
      <ul class="gift-sku-select-bottom-popup__list">
        <li
          v-for="_sku in skus"
          :key="_sku.id"
          class="gift-sku-select-bottom-popup__item"
          :class="{
            'is-selected': _sku.id === selectedId,
            'is-disabled': !isSkuSelectable(_sku),
          }"
          @click="selectSku(_sku)"
        >
          <!-- 占位 -->
          <span v-if="!isSkuSelectable(_sku)" class="gift-sku-select-bottom-popup__item-tag hidden">暂无库存</span>
          <span class="gift-sku-select-bottom-popup__item-name">{{ prizeGroupName }} {{ _sku.name }}</span>
          <span v-if="!isSkuSelectable(_sku)" class="gift-sku-select-bottom-popup__item-tag">暂无库存</span>
        </li>
      </ul>
      <kd-button class="gift-sku-select-bottom-popup__confirm" type="primary" :disabled="!canConfirm" @click="confirm">
        确认选择
      </kd-button>
    </div>
  </kd-popup>
</template>

<script lang="ts">
import { KdButton, KdPopup } from 'kuaidong-ui'
import { BaseComponent, Component, Prop, PropSync, Watch } from 'kuaidong-component'

export interface IGiftSkuOption {
  banner_img?: string
  current_stock?: number
  id: string
  img_url?: string
  name: string
}

@Component({
  components: {
    KdButton,
    KdPopup,
  },
})
export default class GiftSkuSelectBottomPopup extends BaseComponent {
  @PropSync('visible', { type: Boolean, default: false })
  show!: boolean

  @Prop({ type: String, default: '' })
  prizeGroupName!: string

  @Prop({ type: Array, default: () => [] })
  skus!: IGiftSkuOption[]

  selectedId = ''

  popupVisible = false

  popupHeightFixTimer: ReturnType<typeof setTimeout> | null = null

  get canConfirm() {
    return !!this.selectedId && this.skus.some((_s) => _s.id === this.selectedId && this.isSkuSelectable(_s))
  }

  beforeDestroy() {
    this.clearPopupHeightFixTimer()
  }

  @Watch('show')
  onShowChange(_visible: boolean) {
    if (_visible) {
      const _firstAvailable = this.skus.find((_s) => this.isSkuSelectable(_s))
      this.selectedId = _firstAvailable?.id ?? ''
      this.openPopup()
      return
    }
    this.popupVisible = false
  }

  @Watch('popupVisible')
  onPopupVisibleChange(_visible: boolean) {
    if (!_visible && this.show) {
      this.show = false
    }
  }

  openPopup() {
    this.popupVisible = false
    this.$nextTick(() => {
      if (!this.show) {
        return
      }
      this.popupVisible = true
      this.scheduleFixPopupHeight()
    })
  }

  onPopupDisplay() {
    this.scheduleFixPopupHeight()
  }

  scheduleFixPopupHeight() {
    this.$nextTick(() => this.fixPopupMainHeight())
    this.clearPopupHeightFixTimer()
    this.popupHeightFixTimer = setTimeout(() => {
      this.fixPopupMainHeight()
      this.popupHeightFixTimer = null
    }, 450)
  }

  clearPopupHeightFixTimer() {
    if (this.popupHeightFixTimer) {
      clearTimeout(this.popupHeightFixTimer)
      this.popupHeightFixTimer = null
    }
  }

  fixPopupMainHeight() {
    const _main = this.$el?.querySelector?.('.kd-popup__main') as HTMLElement | null
    if (_main && this.popupVisible) {
      _main.style.height = ''
    }
  }

  isSkuSelectable(_sku: IGiftSkuOption) {
    return (_sku.current_stock ?? 0) > 0
  }

  selectSku(_sku: IGiftSkuOption) {
    if (!this.isSkuSelectable(_sku)) {
      return
    }
    this.selectedId = _sku.id
  }

  confirm() {
    if (!this.canConfirm) {
      this.toast('请选择有库存的规格')
      return
    }
    this.popupVisible = false
    this.show = false
    this.$emit('confirm', this.selectedId)
  }
}
</script>

<style lang="less">
.kd-popup.gift-sku-select-bottom-popup {
  > .kd-popup__main {
    height: auto !important;
    background: #fff;

    > .kd-popup__content {
      padding: 0;
      background: #fff;
      border-radius: 16px 16px 0 0;
    }
  }

  .gift-sku-select-bottom-popup__panel {
    position: relative;
    padding: 8px 20px 24px;
    border-radius: 16px 16px 0 0;
    background: #fff;
  }

  .gift-sku-select-bottom-popup__handle {
    width: 38px;
    height: 4px;
    margin: 0 auto 16px;
    border-radius: 2px;
    background: #dcdde0;
  }

  .gift-sku-select-bottom-popup__title {
    margin: 0 0 12px;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    text-align: center;
    color: #000;
  }

  .gift-sku-select-bottom-popup__list {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 280px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .gift-sku-select-bottom-popup__item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    border-radius: 10px;
    background: #f3f3f3;
    font-size: 14px;
    font-weight: 500;
    line-height: 48px;
    text-align: center;
    color: #797979;
    border: 1px solid transparent;
    box-sizing: border-box;

    &.is-selected {
      background: #edf7ff;
      border-color: #0289f0;
      color: #0380ee;
    }

    &.is-disabled {
      background: #f5f5f5;
    }
  }

  .is-disabled .gift-sku-select-bottom-popup__item-name {
    opacity: 0.2;
  }

  .gift-sku-select-bottom-popup__item-tag {
    flex: 0 0 auto;
    height: 22px;
    padding: 0 8px;
    border-radius: 4px;
    background: #ebebeb;
    font-size: 10px;
    font-weight: 400;
    line-height: 22px;
    color: #afafaf;
    margin: 0 8px;

    &.hidden {
      visibility: hidden;
    }
  }

  .gift-sku-select-bottom-popup__confirm {
    display: block;
    width: 260px;
    height: 44px;
    margin: 20px auto 0;
    border: none;
    border-radius: 22px;
    background: linear-gradient(156deg, #0855e5 3.66%, #0096f3 69.71%);
    color: #fff;
    font-size: 15px;
    font-weight: 500;
  }
}
</style>
